import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import {
  authService,
  User,
  AuthSession,
  PasswordChangeRequired,
  MfaSetupRequired,
  MfaCodeRequired,
} from '@/services/authService';

interface AuthContextType {
  user: User | null;
  session: AuthSession | null;
  loading: boolean;
  /** A session is ending: the page reloads as soon as the revoke is done. */
  signingOut: boolean;
  error: string | null;
  passwordChangeRequired: PasswordChangeRequired | null;
  mfaSetupRequired: MfaSetupRequired | null;
  mfaCodeRequired: MfaCodeRequired | null;
  signIn: (email: string, password: string) => Promise<void>;
  completePasswordChange: (newPassword: string, fullName: string) => Promise<void>;
  completeMfaSetup: (code: string) => Promise<void>;
  submitMfaCode: (code: string) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  confirmPasswordReset: (email: string, code: string, newPassword: string) => Promise<void>;
  signOut: () => Promise<void>;
  isAuthenticated: boolean;
  hasRole: (role: string) => boolean;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/** Refresh the tokens this often, and whenever fewer than 5 minutes are left. */
const REFRESH_EVERY_MS = 30_000;
const REFRESH_AHEAD_MS = 5 * 60 * 1000;

/**
 * The sign-in state for the page. `authService` owns the session; this
 * context follows it, so a session handed over by another tab, or ended in
 * another tab, shows here without a page load.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [signingOut, setSigningOut] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [passwordChangeRequired, setPasswordChangeRequired] = useState<PasswordChangeRequired | null>(null);
  const [mfaSetupRequired, setMfaSetupRequired] = useState<MfaSetupRequired | null>(null);
  const [mfaCodeRequired, setMfaCodeRequired] = useState<MfaCodeRequired | null>(null);

  // Follow the auth service, and wait for the open tabs to hand over a session.
  useEffect(() => {
    let cancelled = false;
    const sync = () => {
      if (cancelled) return;
      setSession(authService.getSignedInSession());
      setSigningOut(authService.isEnding());
    };
    const unsubscribe = authService.subscribe(sync);
    authService.ready
      .then(() => authService.ensureValidSession(REFRESH_AHEAD_MS))
      .catch((err) => console.warn('[auth] First token check failed:', err))
      .finally(() => {
        sync();
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  // Keep the tokens fresh while signed in. Refreshing is not input.
  const sid = session?.sid;
  useEffect(() => {
    if (!sid) return;
    const id = window.setInterval(() => {
      void authService.ensureValidSession(REFRESH_AHEAD_MS);
    }, REFRESH_EVERY_MS);
    return () => window.clearInterval(id);
  }, [sid]);

  // On return to the tab: check the limits at once, then refresh after the
  // network has had 2 seconds to come back from a laptop wake.
  const visibilityDebounceRef = useRef<number | null>(null);
  useEffect(() => {
    const onReturn = () => {
      if (document.visibilityState !== 'visible') return;
      if (!authService.getSignedInSession()) return;

      if (visibilityDebounceRef.current) window.clearTimeout(visibilityDebounceRef.current);
      visibilityDebounceRef.current = window.setTimeout(() => {
        visibilityDebounceRef.current = null;
        if (!navigator.onLine) {
          const onlineHandler = () => {
            window.removeEventListener('online', onlineHandler);
            void authService.ensureValidSession(REFRESH_AHEAD_MS);
          };
          window.addEventListener('online', onlineHandler);
          return;
        }
        void authService.ensureValidSession(REFRESH_AHEAD_MS);
      }, 2000);
    };

    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('pageshow', onReturn);
    window.addEventListener('focus', onReturn);
    return () => {
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('pageshow', onReturn);
      window.removeEventListener('focus', onReturn);
      if (visibilityDebounceRef.current) window.clearTimeout(visibilityDebounceRef.current);
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    setPasswordChangeRequired(null);

    try {
      const result = await authService.signIn(email, password);

      if ('requiresPasswordChange' in result) {
        setPasswordChangeRequired(result);
      } else if ('requiresMfaSetup' in result) {
        setMfaSetupRequired(result);
      } else if ('requiresMfaCode' in result) {
        setMfaCodeRequired(result);
      } else {
        setSession(result);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Authentication failed';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const completePasswordChange = async (newPassword: string, fullName: string) => {
    if (!passwordChangeRequired) {
      throw new Error('No password change session available');
    }

    setLoading(true);
    setError(null);

    try {
      const result = await authService.completePasswordChange(
        passwordChangeRequired.session,
        passwordChangeRequired.username,
        newPassword,
        fullName
      );

      if ('requiresMfaSetup' in result) {
        setMfaSetupRequired(result);
        setMfaCodeRequired(null);
      } else if ('requiresMfaCode' in result) {
        setMfaCodeRequired(result);
      } else {
        setSession(result);
      }
      setPasswordChangeRequired(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Password change failed';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const completeMfaSetup = async (code: string) => {
    if (!mfaSetupRequired) {
      throw new Error('No MFA setup session available');
    }

    setLoading(true);
    setError(null);

    try {
      const authSession = await authService.completeMfaSetupAndSignIn(
        mfaSetupRequired.username,
        mfaSetupRequired.session,
        code
      );
      setSession(authSession);
      setMfaSetupRequired(null);
      setMfaCodeRequired(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'MFA setup failed';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const submitMfaCode = async (code: string) => {
    if (!mfaCodeRequired) {
      throw new Error('No MFA challenge session available');
    }

    setLoading(true);
    setError(null);

    try {
      const authSession = await authService.respondToMfaCodeAndSignIn(
        mfaCodeRequired.username,
        mfaCodeRequired.session,
        code
      );
      setSession(authSession);
      setMfaSetupRequired(null);
      setMfaCodeRequired(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Invalid MFA code';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Ends the session in every tab, then reloads the page.
  const signOut = async () => {
    setError(null);
    await authService.signOut();
  };

  const requestPasswordReset = async (email: string) => {
    setLoading(true);
    setError(null);

    try {
      await authService.requestPasswordReset(email);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Password reset request failed';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const confirmPasswordReset = async (email: string, code: string, newPassword: string) => {
    setLoading(true);
    setError(null);

    try {
      await authService.confirmPasswordReset(email, code, newPassword);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Password reset confirmation failed';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => setError(null);

  // From the same state as isAuthenticated, so the two never disagree for a render.
  const hasRole = (role: string): boolean => {
    return session?.user.groups.includes(role) ?? false;
  };

  const value: AuthContextType = {
    user: session?.user || null,
    session,
    loading,
    signingOut,
    error,
    passwordChangeRequired,
    mfaSetupRequired,
    mfaCodeRequired,
    signIn,
    completePasswordChange,
    completeMfaSetup,
    submitMfaCode,
    requestPasswordReset,
    confirmPasswordReset,
    signOut,
    isAuthenticated: session !== null,
    hasRole,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
